import CloseIcon from "@mui/icons-material/Close";
import {
  Avatar,
  CardContent,
  CardContentProps,
  CardHeader,
  CardHeaderProps,
  CircularProgress,
  IconButton,
  Theme,
  Typography,
  useTheme,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { Map } from "ol";
import React from "react";
import { useTranslation } from "react-i18next";
import { useMeasure } from "react-use";
import { UseMeasureRef } from "react-use/lib/useMeasure";
import {
  setCesiumPopupPosition,
  setHoveredCesiumItem,
  setSelectedCesiumItem,
} from "~/redux/reducers/mapReducer";
import { useAppDispatch } from "~/redux/store/store";

type Props = {
  avatarText?: string | JSX.Element;
  title?: React.ReactNode;
  subheader?: string;
  map?: Map | undefined;
  content?: JSX.Element;
  clicked?: boolean;
  loading?: boolean;
  contentProps?: CardContentProps;
  headerProps?: CardHeaderProps;
  hideCloseIcon?: boolean;
  useHoverContent?: boolean;
  avatarColor?: string;
};

export const usePopupStyles = makeStyles((theme: Theme) => ({
  avatar: {
    color: theme.palette.getContrastText(theme.palette.primary.main),
    backgroundColor: theme.palette.primary.main,
    fontSize: theme.typography.pxToRem(10),
    width: theme.spacing(6),
    height: theme.spacing(6),
  },
  content: {
    maxHeight: 400,
    minWidth: 300,
    overflowY: "auto",
  },
  action: {
    marginLeft: "auto",
  },
}));

export const GenericPopup = ({
  map,
  avatarText,
  title,
  subheader,
  content,
  clicked,
  loading,
  contentProps,
  headerProps,
  hideCloseIcon = false,
  useHoverContent,
  avatarColor,
}: Props): JSX.Element => {
  const classes = usePopupStyles();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const theme = useTheme();
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const featureCount = React.useMemo(
    () => map?.getOverlayById("popup")?.get("featureCount") ?? 1,
    [map],
  );

  const [sizeRef, { width, height }] = useMeasure();

  React.useEffect(() => {
    if (!clicked) return;
    map
      ?.getOverlayById("popup")
      .panIntoView({ animation: { duration: 300 }, margin: 30 });
  }, [clicked, map, width, height]);

  return (
    <>
      <CardHeader
        avatar={
          avatarText ? (
            <Avatar
              sx={{
                backgroundColor: avatarColor
                  ? `${avatarColor} !important`
                  : theme.palette.primary.main,
              }}
              className={classes.avatar}
            >
              {loading ? (
                <CircularProgress size="32px" color="secondary" />
              ) : (
                avatarText
              )}
            </Avatar>
          ) : null
        }
        classes={{ action: classes.action }}
        title={title ?? null}
        subheader={subheader ?? null}
        titleTypographyProps={{
          noWrap: true,
          title: typeof title === "string" ? title : undefined,
        }}
        subheaderTypographyProps={{ noWrap: true, title: subheader }}
        action={
          hideCloseIcon ? null : (
            <IconButton
              onClick={() => {
                if (map) {
                  const ol = map?.getOverlayById("popup");
                  ol?.setPosition(undefined);
                  ol?.set("clickedOpen", false);
                } else {
                  dispatch(setCesiumPopupPosition(undefined));
                  dispatch(setSelectedCesiumItem(undefined));
                  dispatch(setHoveredCesiumItem(undefined));
                }
              }}
              size="large"
            >
              <CloseIcon />
            </IconButton>
          )
        }
        {...headerProps}
      />

      {useHoverContent && <CardContent> {content}</CardContent>}

      {!clicked && featureCount > 1 ? (
        <CardContent className={classes.content} {...contentProps}>
          <Typography variant="body2" align="center">
            {featureCount > 2
              ? t("map:feature-count-multiple", { count: featureCount - 1 })
              : t("map:feature-count")}
          </Typography>
        </CardContent>
      ) : null}
      {clicked && content && (
        <CardContent className={classes.content} {...contentProps}>
          <div ref={sizeRef as UseMeasureRef<HTMLDivElement>}>{content}</div>
        </CardContent>
      )}
    </>
  );
};
